import { createSelector } from '@reduxjs/toolkit'
import { Country } from '@afterpaytouch/portal-api/types'
import { CardExpiryStatus, PaymentMethodType } from '@afterpay/types'
import { CheckoutAppSelector, TopazState, useAppSelector, useSavedBankAccounts, useSavedCards } from '../'
import {
  isSavedBankAccountValid,
  isSavedCard,
  isSavedCardValid,
  SelectedPaymentMethod,
  ThreeDSState,
  TopazPaymentState,
  TopazStatus,
  EnrichedCardDetails,
  isNewCard,
} from '../../model'
import { getCardBrandFromCardNumber } from '@afterpaytouch/form/lib/fields/Card/utils'
import { topazSetupService } from '@afterpaytouch/topaz-api/setup'
import { LastSuccessfulSetup } from './reducer'
import { isNullOrUndefined } from '@afterpay/utils'

export const topazSelector: CheckoutAppSelector<TopazState> = (state) => state.topaz

export const purchasePaymentStateSelector: CheckoutAppSelector<TopazPaymentState> = createSelector(topazSelector, (state) => state.purchase)

export const purchaseStatusSelector: CheckoutAppSelector<TopazStatus> = createSelector(purchasePaymentStateSelector, (state) => state.status)

export const selectedPaymentMethod: CheckoutAppSelector<SelectedPaymentMethod | undefined> = createSelector(
  purchasePaymentStateSelector,
  (state) => state.selected
)

export const isApplePay: CheckoutAppSelector<boolean> = createSelector(selectedPaymentMethod, (state) => state?.type === 'APPLE_PAY')

export const threeDsStateSelector: CheckoutAppSelector<ThreeDSState | undefined> = createSelector(topazSelector, (state) => state.purchase.threeDS)

export const lastSuccessfulSetupSelector: CheckoutAppSelector<LastSuccessfulSetup | undefined> = createSelector(
  topazSelector,
  (state) => state.lastSuccessfulSetup
)

export const hasUserInitiatedChangedPaymentMethodSelector: CheckoutAppSelector<boolean> = createSelector(
  topazSelector,
  (state) => state.hasUserInitiatedChangedPaymentMethod
)

export const {
  endpoints: { setupPayment, setupOrder },
} = topazSetupService

export const useIsApplePay = (): boolean => useAppSelector(isApplePay)

export const useSelectedPaymentMethod = (): SelectedPaymentMethod | undefined => useAppSelector(selectedPaymentMethod)

export const useTopazState = (): TopazState => useAppSelector(topazSelector)

export const useThreeDsState = (): ThreeDSState | undefined => useAppSelector(threeDsStateSelector)

export const usePurchaseState = (): TopazPaymentState => useAppSelector(purchasePaymentStateSelector)

export const useHasFailedToRetrieveCards = (): boolean => useAppSelector(purchaseStatusSelector) === TopazStatus.FailedToRetrieveNewCard

export const useLastSuccessfulSetup = (): LastSuccessfulSetup | undefined => useAppSelector(lastSuccessfulSetupSelector)

export const useHasUserInitiatedChangedPaymentMethod = (): boolean => useAppSelector(hasUserInitiatedChangedPaymentMethodSelector)

export const useIsSelectedPaymentMethodValid = (): ((selectedPaymentMethod: SelectedPaymentMethod) => boolean) => {
  const savedCards = useSavedCards()
  const savedBankAccounts = useSavedBankAccounts()
  return (selectedPaymentMethod: SelectedPaymentMethod) => {
    if (selectedPaymentMethod === undefined) {
      return false
    }
    switch (selectedPaymentMethod.type) {
      case PaymentMethodType.APPLE_PAY:
        return true
      case PaymentMethodType.CREDIT_CARD:
        if ('card' in selectedPaymentMethod && isSavedCard(selectedPaymentMethod.card)) {
          return isSavedCardValid(selectedPaymentMethod.card, savedCards)
        } else {
          return true
        }
      case PaymentMethodType.ACH_BANK_ACCOUNT:
        return 'account' in selectedPaymentMethod && isSavedBankAccountValid(selectedPaymentMethod.account, savedBankAccounts)
      default:
        return false
    }
  }
}

export const useSelectedCard = (): (Omit<EnrichedCardDetails, 'cardBrand'> & { cardBrand?: EnrichedCardDetails['cardBrand'] }) | undefined => {
  const selectedPaymentMethod = useSelectedPaymentMethod()
  const savedCards = useSavedCards()

  if (isNullOrUndefined(selectedPaymentMethod) || selectedPaymentMethod.type !== PaymentMethodType.CREDIT_CARD) {
    return
  }

  const selectedCard = selectedPaymentMethod.card

  if (isSavedCard(selectedCard) && isSavedCardValid(selectedCard, savedCards)) {
    return savedCards.find((savedCard) => savedCard.token === selectedCard.token)
  } else if (isNewCard(selectedCard)) {
    const expiryYear = parseFloat(selectedCard.cardExpiryYear)
    const expiryMonth = parseFloat(selectedCard.cardExpiryMonth)
    const expiryDate = `${selectedCard.cardExpiryMonth}/${selectedCard.cardExpiryYear}`
    return {
      cardholderName: selectedCard.cardHolderName,
      maskedPan: selectedCard.cardNumber,
      expiryYear,
      expiryMonth,
      expiryDate,
      token: '',
      cvvRequired: false,
      preferred: false,
      cardBrand: getCardBrandFromCardNumber(selectedCard.cardNumber),
      creditCardType: selectedCard.creditCardType,
      countryCode: selectedCard.address?.country as Country,
      cardExpiryStatus: CardExpiryStatus.OK,
      error: null,
      paymentMethodType: PaymentMethodType.CREDIT_CARD,
      issuingBank: '',
    }
  }
}
